import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  Button,
  Checkbox,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerHeaderTitle,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Switch,
  Textarea
} from '@fluentui/react-components';
import { Trans, useTranslation } from 'react-i18next';

// models
import {
  RelationShipTypeCreateCommand,
  RelationShipTypeUpdateCommand
} from '../../../models/Entities/Person/RelationshipType/RelationshipTypeModels';

// components
import { ErrorMessageComponent } from '../../Error/ErrorMessageComponent';

// functions
import * as ownFunctions from './RelationshipType.Form.Functions';

interface FormValidation {
  success: boolean;
  errors: string[];
}

const RelationshipTypeFormComponent = (props: ownFunctions.Props) => {
  const [t] = useTranslation();

  const [submitted, setSubmitted] = useState<boolean>(false);
  const [validation, setValidation] = useState<FormValidation | undefined>();

  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [isForNaturalPersons, setIsForNaturalPersons] = useState<boolean>(true);
  const [isForLegalPersons, setIsForLegalPersons] = useState<boolean>(false);
  const [isActive, setIsActive] = useState<boolean>(true);

  useEffect(() => {
    if (props.relationshipType) {
      setName(props.relationshipType.name || '');
      setDescription(props.relationshipType.description || '');
      setIsForNaturalPersons(props.relationshipType.isForNaturalPersons || false);
      setIsForLegalPersons(props.relationshipType.isForLegalPersons || false);
      setIsActive(props.relationshipType.isActive ?? true);
    } else {
      resetForm();
    }
  }, [props.relationshipType, props.isOpen]);

  useEffect(() => {
    if (submitted && (props.successAdding || props.successUpdating)) {
      props.GetRelationshipTypes();
      resetForm();
      props.dismissPanel();
    }
  }, [props.successAdding, props.successUpdating]);

  const resetForm = () => {
    setName('');
    setDescription('');
    setIsForNaturalPersons(true);
    setIsForLegalPersons(false);
    setIsActive(true);
    setSubmitted(false);
    setValidation(undefined);
  };

  const validate = (): boolean => {
    const errors: string[] = [];

    if (!name.trim()) {
      errors.push(t('persons.relationshipTypes.form.validation.name', { defaultValue: 'El nombre es obligatorio' }));
    }

    if (!isForNaturalPersons && !isForLegalPersons) {
      errors.push(
        t('persons.relationshipTypes.form.validation.personType', {
          defaultValue: 'Debe aplicar al menos a un tipo de persona'
        })
      );
    }

    const result: FormValidation = { success: errors.length === 0, errors };
    setValidation(result);
    return result.success;
  };

  const handleSave = () => {
    setSubmitted(true);
    if (!validate()) return;

    const payload: RelationShipTypeCreateCommand = {
      name: name.trim(),
      description: description.trim() || null,
      isActive,
      isForLegalPersons,
      isForNaturalPersons
    };

    if (props.relationshipType) {
      props.UpdateRelationshipType({
        ...payload,
        id: props.relationshipType.id
      } as RelationShipTypeUpdateCommand);
    } else {
      props.CreateRelationshipType(payload);
    }
  };

  const handleCancel = () => {
    props.dismissPanel();
    resetForm();
  };

  return (
    <Drawer
      open={props.isOpen}
      type="overlay"
      position="end"
      size="small"
      onOpenChange={(_, data) => {
        if (!data.open) handleCancel();
      }}
    >
      <DrawerHeader>
        <DrawerHeaderTitle>
          {props.relationshipType
            ? t('persons.relationshipTypes.form.editTitle', { defaultValue: 'Editar tipo de relación' })
            : t('persons.relationshipTypes.form.addTitle', { defaultValue: 'Nuevo tipo de relación' })}
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody>
        {submitted && validation && !validation.success && (
          <MessageBar intent="error" layout="multiline" style={{ marginBottom: 12 }}>
            <MessageBarBody>
              <MessageBarTitle>
                {t('persons.form.review', { defaultValue: 'Revise la información ingresada' })}
              </MessageBarTitle>
              <ul>
                {validation.errors.map((error: string, index: number) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </MessageBarBody>
          </MessageBar>
        )}

        {submitted && (props.failAdding || props.failUpdating) && props.error && (
          <ErrorMessageComponent Error={props.error} />
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Field
            label={t('persons.relationshipTypes.form.name', { defaultValue: 'Nombre' })}
            required
            validationMessage={
              submitted && !name.trim()
                ? t('persons.relationshipTypes.form.validation.name', { defaultValue: 'El nombre es obligatorio' })
                : undefined
            }
            validationState={submitted && !name.trim() ? 'error' : undefined}
          >
            <Input value={name} onChange={(_, data) => setName(data.value || '')} />
          </Field>

          <Field label={t('persons.relationshipTypes.form.description', { defaultValue: 'Descripción' })}>
            <Textarea value={description} onChange={(_, data) => setDescription(data.value || '')} />
          </Field>

          <Checkbox
            label={t('persons.relationshipTypes.form.isForNaturalPersons', {
              defaultValue: 'Aplica a personas físicas'
            })}
            checked={isForNaturalPersons}
            onChange={(_, data) => setIsForNaturalPersons(!!data.checked)}
          />

          <Checkbox
            label={t('persons.relationshipTypes.form.isForLegalPersons', {
              defaultValue: 'Aplica a personas jurídicas'
            })}
            checked={isForLegalPersons}
            onChange={(_, data) => setIsForLegalPersons(!!data.checked)}
          />

          <Switch
            label={t('persons.relationshipTypes.form.isActive', { defaultValue: 'Activo' })}
            checked={isActive}
            onChange={(_, data) => setIsActive(!!data.checked)}
          />
        </div>
      </DrawerBody>

      <DrawerFooter>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-start', width: '100%' }}>
          <Button appearance="secondary" onClick={handleCancel}>
            <Trans i18nKey={'persons.form.footer.cancel'} defaultValue="Cancelar" />
          </Button>
          <Button appearance="primary" onClick={handleSave} disabled={props.isAdding || props.isUpdating}>
            <Trans i18nKey={'persons.form.footer.save'} defaultValue="Guardar" />
          </Button>
        </div>
      </DrawerFooter>
    </Drawer>
  );
};

export default connect(
  ownFunctions.mapStateToProps,
  ownFunctions.mapDispatchToProps
)(RelationshipTypeFormComponent as any);
